import { useEffect, useState } from 'react';
import api from '../api/axiosConfig';

// Shared premium status: fetched once and cached at module scope so the
// many gated buttons/views don't each hit /billing/status/. Assume premium
// until known so paying users never see an upgrade flash.

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

const fetchPremium = (): Promise<boolean> => {
    if (inflight) return inflight;
    inflight = api.get('/billing/status/')
        .then((res) => {
            cached = !!res.data.premium;
            listeners.forEach((l) => l(cached!));
            return cached;
        })
        .catch(() => {
            // On error, don't nag: treat as premium
            cached = true;
            return true;
        })
        .finally(() => { inflight = null; });
    return inflight;
};

/** Call after a successful upgrade so gated UI unlocks without a reload. */
export const refreshPremium = () => {
    cached = null;
    fetchPremium();
};

export function usePremium(): { premium: boolean; loading: boolean } {
    const [premium, setPremium] = useState<boolean>(cached ?? true);
    const [loading, setLoading] = useState<boolean>(cached === null);

    useEffect(() => {
        let active = true;
        const update = (v: boolean) => { if (active) { setPremium(v); setLoading(false); } };
        listeners.add(update);
        if (cached !== null) {
            update(cached);
        } else {
            fetchPremium().then(update);
        }
        return () => { active = false; listeners.delete(update); };
    }, []);

    return { premium, loading };
}
